import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import HttpService from "../services/HttpService";

const pageSizeOptions = [25, 50, 100];

export default function CardInventoryOperations() {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [selectedOperation, setSelectedOperation] = useState(null);

  const { data, isLoading, isFetching, isError } = useQuery(
    ["cardInventoryOperations", page, pageSize],
    async () =>
      (
        await HttpService.getAxiosClient().get(
          `${window.API_URL}/card-inventory/operations?page=${page}&pageSize=${pageSize}`
        )
      ).data,
    { keepPreviousData: true }
  );

  const detailQuery = useQuery(
    ["cardInventoryOperation", selectedOperation],
    async () =>
      (
        await HttpService.getAxiosClient().get(
          `${window.API_URL}/card-inventory/operations/${selectedOperation}`
        )
      ).data,
    { enabled: selectedOperation !== null }
  );

  const rows = (data?.items || []).map((item) => ({ ...item, id: item.id }));
  const columns = [
    { field: "id", headerName: "Operation", width: 110 },
    { field: "operationType", headerName: "Type", width: 170 },
    { field: "previousInventoryStatus", headerName: "Previous status", width: 160 },
    { field: "newInventoryStatus", headerName: "New status", width: 150 },
    { field: "createdBy", headerName: "Created by", width: 180 },
    {
      field: "createdAt",
      headerName: "Created",
      width: 190,
      valueGetter: ({ value }) => (value ? new Date(value).toLocaleString() : ""),
    },
    { field: "detailCount", headerName: "Cards", width: 90 },
    {
      field: "actions",
      headerName: "",
      width: 130,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Button size="small" onClick={() => setSelectedOperation(row.id)}>
          Details
        </Button>
      ),
    },
  ];

  const detailColumns = [
    { field: "id", headerName: "Detail", width: 90 },
    {
      field: "operationDate",
      headerName: "Operation date",
      width: 180,
      valueGetter: ({ value }) => (value ? new Date(value).toLocaleString() : ""),
    },
    { field: "idBundleDetail", headerName: "Card detail", width: 120 },
    { field: "cardProxy", headerName: "Card proxy", width: 220 },
    { field: "previousInventoryStatus", headerName: "Previous", width: 130 },
    { field: "newInventoryStatus", headerName: "New", width: 120 },
    { field: "currentInventoryStatus", headerName: "Current", width: 120 },
    {
      field: "currentStatusMatchesOperation",
      headerName: "Current matches",
      width: 130,
      type: "boolean",
    },
  ];

  const exportDetailsCsv = () => {
    const details = detailQuery.data?.details || [];
    const escape = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
    const header = [
      "Detail",
      "Operation date",
      "Card detail",
      "Card proxy",
      "Previous status",
      "New status",
      "Current status",
      "Current status matches",
    ];
    const lines = details.map((detail) =>
      [
        detail.id,
        detail.operationDate
          ? new Date(detail.operationDate).toISOString()
          : "",
        detail.idBundleDetail,
        detail.cardProxy,
        detail.previousInventoryStatus,
        detail.newInventoryStatus,
        detail.currentInventoryStatus,
        detail.currentStatusMatchesOperation,
      ]
        .map(escape)
        .join(",")
    );
    const blob = new Blob([[header.map(escape).join(","), ...lines].join("\r\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `inventory-operation-${selectedOperation}-details.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ margin: 0, padding: "24px 80px" }}>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5">Inventory operations</Typography>
        <Typography color="text.secondary">
          {data?.total || 0} operation(s)
        </Typography>
      </Stack>
      {isError && <Typography color="error">Unable to load inventory operations.</Typography>}
      <Box sx={{ height: 620, width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={isLoading || isFetching}
          pagination
          paginationMode="server"
          page={page}
          pageSize={pageSize}
          rowCount={data?.total || 0}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(0);
          }}
          rowsPerPageOptions={pageSizeOptions}
          components={{ Toolbar: GridToolbar }}
        />
      </Box>
      <Dialog
        open={selectedOperation !== null}
        onClose={() => setSelectedOperation(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Inventory operation details</DialogTitle>
        <DialogContent>
          {detailQuery.isLoading && <Typography>Loading details...</Typography>}
          {detailQuery.isError && (
            <Typography color="error">Unable to load operation details.</Typography>
          )}
          {detailQuery.data && (
            <>
              <Stack direction="row" spacing={4} sx={{ mb: 1 }}>
                <Typography>
                  <b>Operation:</b> {detailQuery.data.operation.id}
                </Typography>
                <Typography>
                  <b>Transition:</b>{" "}
                  {detailQuery.data.operation.previousInventoryStatus} →{" "}
                  {detailQuery.data.operation.newInventoryStatus}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={4} sx={{ mb: 2 }}>
                <Typography>
                  <b>Date:</b>{" "}
                  {new Date(
                    detailQuery.data.operation.createdAt
                  ).toLocaleString()}
                </Typography>
                <Typography>
                  <b>Notes:</b>{" "}
                  {detailQuery.data.operation.notes || "No notes"}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={exportDetailsCsv}
                >
                  Save details as CSV
                </Button>
              </Stack>
              <Box sx={{ height: 420 }}>
                <DataGrid
                  rows={detailQuery.data.details}
                  columns={detailColumns}
                  disableSelectionOnClick
                />
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
